import { faArrowLeft, faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, Form, Formik, FormikProps } from 'formik';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { ipHolderDelete } from '../../../api/actions/ip-holder/ip-holder-delete';
import { getIpHolder } from '../../../api/actions/ip-holder/ip-holder-get';
import { ipHolderSave } from '../../../api/actions/ip-holder/ip-holder-save';
import { Block } from '../../../components/shared/block/block';
import { AddBreadcrumbsItem } from '../../../components/shared/breadcrumbs/breadcrumbs';
import { Button } from '../../../components/shared/button/button';
import { LinkButton } from '../../../components/shared/link-button/link-button';
import { Loader } from '../../../components/shared/loader/loader';
import { DeleteQuestionPopup } from '../../../components/shared/popup/delete-question-popup';
import { basePerformError } from '../../../helpers/error-helpers';
import { compareText } from '../../../helpers/array-helpers';
import { getParentPath, joinPath } from '../../../helpers/path-helpers';
import { IIpHolder } from '../../../models/ip-holder';
import { INarrative } from '../../../models/narrative';
import { IUser } from '../../../models/user';
import { IDictionary } from '../../../types/dictionary';
import styles from './ip-holder-editor.module.scss';


enum FormFields {
    identifier = 'identifier',
    name = 'name',
    url = 'url',
    details = 'details',
    blockPhotoActivity = 'blockPhotoActivity',
}

interface IForm {
    [FormFields.identifier]: string,
    [FormFields.name]: string,
    [FormFields.url]?: string,
    [FormFields.details]?: string,
    [FormFields.blockPhotoActivity]?: boolean,
}

export function IpHolderEditor(props: RouteComponentProps<{uid: string}>) {

    const [loading, setLoading] = useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [ipHolder, setIpHolder] = useState<IIpHolder>();

    const loadData = useCallback(async (uid?: string) => {
        console.log('loadData');
        try {
            if(uid !== undefined || (props.match.params.uid && props.match.params.uid !== '_')) {
                let uidToLoad;
                if (uid !== undefined) uidToLoad = uid;
                else uidToLoad = props.match.params.uid;
                console.log('ip holder editor; loading data for uid: ' + uidToLoad);
                setIpHolder(await getIpHolder(uidToLoad));
            }
            else {
                console.log('ip holder editor; loading empty data');
                setIpHolder({} as IIpHolder);
            }
        }
        catch(err) {
            basePerformError(err, props.history);
        }
    }, [props.history, props.match.params.uid]);

    useEffect(() => {
        console.log('ip holder editor; useEffect');
        loadData().catch(() => {/** */});
    }, []);

    const handleSubmit = async (
        values: IForm,
        { setSubmitting }: { setSubmitting: (status: boolean) => void, setErrors: (errors: IDictionary<string>) => void },
    ) => {
        setLoading(true);
        try {
            const newIpHolder: Partial<IIpHolder> = {
                uid: ipHolder?.uid,
                identifier: values.identifier,
                name: values.name,
                url: values.url,
                details: values.details,
                blockPhotoActivity: values.blockPhotoActivity,
            };
            
            const uid = await ipHolderSave(newIpHolder);
            if(!ipHolder?.uid) {
                props.history.push(joinPath(props.match.url.replace(/\/_\/?$/, ''), uid));
            }
            await loadData(uid);
            toast.success('Item has been successfully saved');
        }
        catch (err) {
            basePerformError(err, props.history);
        }
        setSubmitting(false);
        setLoading(false);
    };

    const getValidationSchema = () => {
        return Yup.object<IForm>({
            identifier: Yup.string().label('Identifier').trim().required().min(2).max(50),
            name: Yup.string().label('Name').trim().required().min(2).max(100),
            url: Yup.string().label('URL').trim().url(),
            details: Yup.string().label('Details').trim().max(500),
        });
    };

    const deleteIpHolder = async () => {
        if(!ipHolder?.uid) return;
        await ipHolderDelete(ipHolder?.uid);
        setDeleteDialogVisible(false);
    };

    const renderForm = ({ errors, touched }: FormikProps<IForm>): React.ReactElement => {
        return (
            <div className={styles.formContainer}>
                <Form noValidate>

                    <div className="form-buttons">
                        <div></div>
                        { ipHolder?.uid && (
                            <LinkButton onClick={() => setDeleteDialogVisible(true)} className="red"><FontAwesomeIcon icon={faTrashAlt} /> Delete</LinkButton>
                        )}
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label required">
                                Identifier
                            </div>
                            <Field type="text" name={FormFields.identifier} />
                        </label>
                        <div className="errors">{touched.identifier && errors.identifier}</div>
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label required">
                                Name
                            </div>
                            <Field type="text" name={FormFields.name} />
                        </label>
                        <div className="errors">{touched.name && errors.name}</div>
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label required">
                                URL
                            </div>
                            <Field type="text" name={FormFields.url} />
                        </label>
                        <div className="errors">{touched.url && errors.url}</div>
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label">
                                Details
                            </div>
                            <Field component="textarea" name={FormFields.details} />
                        </label>
                        <div className="errors">{touched.details && errors.details}</div>
                    </div>
                    <div className="form-item">
                        <label>
                            <div className="form-label">
                                Block Photo Activity
                            </div>
                            <Field type="checkbox" name={FormFields.blockPhotoActivity} />
                        </label>
                        <div className="errors">{touched.blockPhotoActivity && errors.blockPhotoActivity}</div>
                    </div>

                    <div className="form-buttons">
                        <Button className="gray" to={getParentPath(props.match.url, 1)}>
                            <FontAwesomeIcon icon={faArrowLeft} /> <span>Back</span>
                        </Button>
                        { loading
                            ? (<Loader />)
                            : (<Button type="submit"><span>Save</span></Button>)
                        }
                    </div>
                </Form>
            </div>
        );
    };

    return !ipHolder ? null : (<>
        <Block className={styles.editor}>

            <AddBreadcrumbsItem
                title="IP Holders"
                url={getParentPath(props.match.url, 1)}
            />
            <AddBreadcrumbsItem
                key={ipHolder.uid}
                title={ipHolder.uid ? `Edit: ${ipHolder.name}` : 'Add IP Holder'}
                url={props.match.url}
            />
            <Formik<IForm>
                initialValues={{
                    identifier: ipHolder?.identifier || '',
                    name: ipHolder?.name || '',
                    url: ipHolder?.url || '',
                    details: ipHolder?.details || '',
                    blockPhotoActivity: ipHolder?.blockPhotoActivity || false,
                }}
                validationSchema={getValidationSchema}
                onSubmit={handleSubmit}
            >
                {renderForm}
            </Formik>
        </Block>

        {!!ipHolder?.uid && (
            <>
                <Block>
                    <div className="list-title">
                        <div>Narratives:
                            <strong>{ (ipHolder?.narratives?.length) || 0 }</strong>
                        </div>
                    </div>

                    { !!ipHolder?.narratives?.length && (
                        <table cellPadding="0" cellSpacing="0" className={`list ${styles.list}`}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Title</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    ipHolder?.narratives?.sort((n1, n2) => compareText(n1, n2, (n) => n.title)).map(((narrative, i) => (
                                        <tr key={narrative.uid}>
                                            <td>
                                                {i + 1}
                                            </td>
                                            <td>
                                                {narrative.title}
                                            </td>
                                            <td>
                                                <LinkButton to={joinPath('/narratives', narrative.uid)}>
                                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                                </LinkButton>
                                            </td>
                                        </tr>
                                    )))
                                }
                            </tbody>
                        </table>
                    )}
                </Block>

                <Block>
                    <div className="list-title">
                        <div>Representatives:
                            <strong>{ (ipHolder?.users?.length) || 0 }</strong>
                        </div>
                    </div>

                    { !!ipHolder?.users?.length && (
                        <table cellPadding="0" cellSpacing="0" className={`list ${styles.list}`}>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    ipHolder?.users?.sort((u1, u2) => {
                                        const name1 = `${u1.firstName || ''} ${u1.lastName || ''}`.trim() || u1.email;
                                        const name2 = `${u2.firstName || ''} ${u2.lastName || ''}`.trim() || u2.email;
                                        return compareText({ name: name1 }, { name: name2 }, (u) => u.name);
                                    }).map(((user, i) => (
                                        <tr key={user.uid}>
                                            <td>
                                                {i + 1}
                                            </td>
                                            <td>
                                                {user.firstName || user.lastName 
                                                    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                                                    : user.email
                                                }
                                            </td>
                                            <td>
                                                {user.email}
                                            </td>
                                            <td>
                                                <LinkButton to={joinPath('/users/users', user.uid)}>
                                                    <FontAwesomeIcon icon={faEdit} /> Edit
                                                </LinkButton>
                                            </td>
                                        </tr>
                                    )))
                                }
                            </tbody>
                        </table>
                    )}
                </Block>
            </>
        )}

        {deleteDialogVisible && (
            <DeleteQuestionPopup
                title="IP Holder Deletion"
                question={<>Are you sure want to delete IP Holder?</>}
                onClose={() => setDeleteDialogVisible(false)}
                onDelete={deleteIpHolder}
                onFinish={() => { props.history.push(getParentPath(props.match.url, 1)) }}
            />
        )}
    </>);
}

